import { useState, useEffect, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const platformUrl = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
function api(fn, slug, opts = {}) { return fetch(`${platformUrl}/functions/v1/${fn}?account=${slug}`, { ...opts, headers: { "Content-Type": "application/json", Authorization: `Bearer ${anonKey}`, ...opts.headers } }).then((r) => r.json()); }

async function fetchPdfBlob(slug, docId) {
  const res = await fetch(`${platformUrl}/functions/v1/download-document?account=${slug}&document_id=${docId}`, { headers: { Authorization: `Bearer ${anonKey}` } });
  if (!res.ok) throw new Error("PDF not available");
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ─── Font loader ─────────────────────────────────────────────────────────────

function injectFonts() {
  [
    { id: "gfont-instrument-serif", href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap" },
    { id: "gfont-dm-sans", href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap" },
  ].forEach((f) => {
    if (!document.getElementById(f.id)) {
      const link = document.createElement("link");
      link.id = f.id; link.rel = "stylesheet"; link.href = f.href;
      document.head.appendChild(link);
    }
  });
}

// ─── Theme ───────────────────────────────────────────────────────────────────

const L = {
  bg: "#fafaf8", text: "#1a1a1a", textSoft: "#444", muted: "#8a8a8a",
  dim: "#c0bdb8", border: "#e5e3de", borderLight: "#f0eee9",
  surface: "#ffffff", inputBg: "#f6f5f2",
};
const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

// ─── Procedure parsing ───────────────────────────────────────────────────────

function extractProcedure(text) {
  const m = text.match(/\[PROCEDURE\]([\s\S]*?)\[\/PROCEDURE\]/);
  if (!m) return null;
  const steps = []; const lines = m[1].trim().split("\n"); let i = 0;
  while (i < lines.length) {
    const sm = lines[i].match(/^\s*(\d+)\.\s*(?:\[p\.(\d+)\])?\s*\*?\*?(.+?)\*?\*?\s*$/);
    if (sm) {
      let content = sm[3].replace(/\*\*/g, "");
      const page = sm[2] ? parseInt(sm[2]) : null;
      i++;
      while (i < lines.length && lines[i].trim() && !/^\s*\d+\./.test(lines[i])) { content += " " + lines[i].trim().replace(/\*\*/g, ""); i++; }
      steps.push({ text: content.trim(), page });
    } else i++;
  }
  return steps.length >= 2 ? steps : null;
}
function stripProcedureMarkers(text) { return text.replace(/\[PROCEDURE\][\s\S]*?\[\/PROCEDURE\]/g, "").trim(); }
function getFirstSourceDoc(sources, docs) { if (!sources?.length || !docs?.length) return null; const s = sources[0]; return docs.find((d) => d.name === s.filename) || null; }

// ─── Markdown Renderer ───────────────────────────────────────────────────────

function RenderMarkdown({ text, fontSize }) {
  const rest = stripProcedureMarkers(text);
  const renderInline = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p);
  const lines = rest.split("\n"); const els = []; let i = 0;
  while (i < lines.length) {
    if (/^\s*\d+\.\s/.test(lines[i])) { const items = []; while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s*/, "").replace(/\*\*/g, "")); i++; } els.push(<ol key={els.length} style={{ margin: "8px 0", paddingLeft: 24, fontSize, lineHeight: 1.7, color: L.text }}>{items.map((it, j) => <li key={j} style={{ marginBottom: 4 }}>{renderInline(it)}</li>)}</ol>); continue; }
    if (/^\s*[-*•]\s/.test(lines[i])) { const items = []; while (i < lines.length && /^\s*[-*•]\s/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*•]\s*/, "").replace(/\*\*/g, "")); i++; } els.push(<ul key={els.length} style={{ margin: "8px 0", paddingLeft: 24, fontSize, lineHeight: 1.7, color: L.text }}>{items.map((it, j) => <li key={j} style={{ marginBottom: 4 }}>{renderInline(it)}</li>)}</ul>); continue; }
    if (!lines[i].trim()) { i++; continue; }
    els.push(<p key={els.length} style={{ margin: "6px 0", fontSize, lineHeight: 1.7, color: L.text }}>{renderInline(lines[i])}</p>); i++;
  }
  return <>{els}</>;
}

// ─── PDF Viewer Overlay ──────────────────────────────────────────────────────

function PDFOverlay({ pdfUrl, page, onClose }) {
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(page || 1);
  const [scale, setScale] = useState(1.0);
  useEffect(() => { if (page && page > 0) setCurrentPage(page); }, [page]);

  const navBtn = { padding: "7px 14px", background: L.inputBg, border: `1px solid ${L.border}`, borderRadius: 8, color: L.text, fontSize: 13, cursor: "pointer", fontFamily: sans, fontWeight: 500 };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "12px 20px", display: "flex", alignItems: "center", gap: 10, background: L.surface, borderBottom: `1px solid ${L.border}`, flexShrink: 0 }}>
        <button onClick={onClose} style={{ ...navBtn, fontWeight: 600 }}>← Close</button>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1} style={navBtn}>←</button>
        <span style={{ color: L.muted, fontSize: 13, minWidth: 80, textAlign: "center" }}>Page {currentPage} / {totalPages}</span>
        <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} style={navBtn}>→</button>
        <div style={{ flex: 1 }} />
        <button onClick={() => setScale((s) => Math.max(0.5, s - 0.2))} style={navBtn}>−</button>
        <span style={{ color: L.muted, fontSize: 12, minWidth: 40, textAlign: "center" }}>{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale((s) => Math.min(3, s + 0.2))} style={navBtn}>+</button>
      </div>
      <div style={{ flex: 1, overflow: "auto", display: "flex", justifyContent: "center", padding: 20, background: L.bg }}>
        <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setTotalPages(numPages)} loading={<div style={{ color: L.muted, padding: 40 }}>Loading PDF...</div>} error={<div style={{ color: "#dc2626", padding: 40 }}>Failed to load PDF</div>}>
          <Page pageNumber={currentPage} scale={scale} renderAnnotationLayer={false} renderTextLayer={false} />
        </Document>
      </div>
    </div>
  );
}

// ─── Procedure + PDF Split View ──────────────────────────────────────────────

function ProcedurePDFView({ steps, pdfUrl, color, onClose }) {
  const [checked, setChecked] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(steps[0]?.page || 1);
  const [totalPages, setTotalPages] = useState(0);
  const stepsRef = useRef(null);
  const progress = Math.round((checked.size / steps.length) * 100);

  const toggle = (i) => {
    setChecked((p) => {
      const n = new Set(p);
      n.has(i) ? n.delete(i) : n.add(i);
      if (!p.has(i) && i + 1 < steps.length && steps[i + 1].page) setCurrentPage(steps[i + 1].page);
      return n;
    });
  };
  const goToStepPage = (step) => { if (step.page) setCurrentPage(step.page); };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 999, background: L.bg, display: "flex", flexDirection: "column", fontFamily: sans }}>
      <div style={{ padding: "12px 20px", background: L.surface, borderBottom: `1px solid ${L.border}`, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <button onClick={onClose} style={{ padding: "6px 14px", background: L.inputBg, border: `1px solid ${L.border}`, borderRadius: 8, color: L.text, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: sans }}>← Back</button>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600, fontFamily: serif, fontStyle: "italic" }}>Procedure</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: progress === 100 ? "#15803d" : L.text }}>
          {progress === 100 ? "Complete" : `${checked.size} / ${steps.length}`}
        </span>
      </div>
      <div style={{ height: 3, background: L.borderLight, flexShrink: 0 }}>
        <div style={{ height: "100%", width: `${progress}%`, background: progress === 100 ? "#16a34a" : L.text, transition: "width 0.3s" }} />
      </div>

      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <div ref={stepsRef} style={{ width: "45%", overflow: "auto", borderRight: `1px solid ${L.border}` }}>
          {steps.map((step, i) => (
            <div key={i} onClick={() => toggle(i)} style={{ display: "flex", gap: 12, padding: "16px 18px", borderBottom: `1px solid ${L.borderLight}`, cursor: "pointer", background: checked.has(i) ? "#f0fdf4" : L.surface, transition: "background 0.15s" }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, background: checked.has(i) ? "#16a34a" : L.inputBg, color: checked.has(i) ? "#fff" : L.text, transition: "all 0.2s", border: checked.has(i) ? "none" : `1px solid ${L.border}` }}>
                {checked.has(i) ? "✓" : i + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, color: L.text, lineHeight: 1.65, textDecoration: checked.has(i) ? "line-through" : "none", opacity: checked.has(i) ? 0.5 : 1 }}>{step.text}</div>
                {step.page && (
                  <button onClick={(e) => { e.stopPropagation(); goToStepPage(step); }} style={{ marginTop: 6, padding: "3px 10px", background: L.inputBg, border: `1px solid ${L.border}`, borderRadius: 6, color: L.textSoft, fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: sans }}>
                    Page {step.page}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div style={{ flex: 1, overflow: "auto", background: "#f0efeb", display: "flex", justifyContent: "center", padding: 16 }}>
          {pdfUrl ? (
            <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setTotalPages(numPages)} loading={<div style={{ color: L.muted, padding: 40 }}>Loading...</div>} error={<div style={{ color: "#dc2626", padding: 40 }}>PDF not available</div>}>
              <Page pageNumber={currentPage} scale={1.0} renderAnnotationLayer={false} renderTextLayer={false} />
            </Document>
          ) : (
            <div style={{ color: L.muted, padding: 40, textAlign: "center" }}>PDF not available for this document.</div>
          )}
        </div>
      </div>

      <div style={{ padding: "10px 20px", background: L.surface, borderTop: `1px solid ${L.border}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 14, flexShrink: 0 }}>
        <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} style={{ padding: "7px 14px", background: L.inputBg, border: `1px solid ${L.border}`, borderRadius: 8, cursor: "pointer", fontFamily: sans, color: L.text }}>←</button>
        <span style={{ fontSize: 13, color: L.muted }}>Page {currentPage}{totalPages ? ` / ${totalPages}` : ""}</span>
        <button onClick={() => setCurrentPage((p) => Math.min(totalPages || 999, p + 1))} style={{ padding: "7px 14px", background: L.inputBg, border: `1px solid ${L.border}`, borderRadius: 8, cursor: "pointer", fontFamily: sans, color: L.text }}>→</button>
      </div>
    </div>
  );
}

// ─── PIN Screen ──────────────────────────────────────────────────────────────

function PinScreen({ slug, onSuccess }) {
  useEffect(() => { injectFonts(); }, []);
  const [pin, setPin] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const tryPin = async (v) => {
    setLoading(true); setError(null);
    try {
      const d = await api("validate-floor-pin", slug, { method: "POST", body: JSON.stringify({ pin: v }) });
      if (d.valid) onSuccess(d);
      else { setError(d.error || "Invalid PIN"); setPin(""); }
    } catch (e) { setError(e.message); setPin(""); }
    setLoading(false);
  };

  const handleKey = (digit) => {
    if (pin.length >= 6) return;
    const n = pin + digit; setPin(n); setError(null);
    if (n.length === 4) setTimeout(() => tryPin(n), 100);
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: L.bg, padding: 24, fontFamily: sans,
    }}>
      <style>{`
        @keyframes pinFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pinDotFill { from { transform: scale(0.5); } to { transform: scale(1); } }
        @keyframes pinShake { 0%,100% { transform: translateX(0); } 20%,60% { transform: translateX(-8px); } 40%,80% { transform: translateX(8px); } }
      `}</style>

      <div style={{ textAlign: "center", animation: "pinFadeIn 0.6s ease both" }}>
        <h1 style={{
          fontSize: 32, fontWeight: 400, margin: "0 0 6px",
          fontFamily: serif, fontStyle: "italic", color: L.text,
        }}>
          Floor Access
        </h1>
        <p style={{ fontSize: 14, color: L.muted, margin: "0 0 40px" }}>
          Enter your team PIN
        </p>

        {/* PIN dots */}
        <div style={{
          display: "flex", gap: 16, marginBottom: 28, justifyContent: "center",
          animation: error ? "pinShake 0.4s ease" : "none",
        }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{
              width: 14, height: 14, borderRadius: "50%",
              border: i < pin.length ? "none" : `2px solid ${L.border}`,
              background: i < pin.length ? L.text : "transparent",
              transition: "all 0.15s",
              animation: i < pin.length ? "pinDotFill 0.15s ease" : "none",
            }} />
          ))}
        </div>

        {error && (
          <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 18, fontWeight: 500 }}>{error}</div>
        )}
        {loading && (
          <div style={{ color: L.muted, fontSize: 13, marginBottom: 18 }}>Verifying...</div>
        )}

        {/* Keypad */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 72px)", gap: 10, justifyContent: "center" }}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, "⌫"].map((k, i) =>
            k === null ? <div key={i} /> : (
              <button
                key={i}
                onClick={() => k === "⌫" ? setPin((p) => p.slice(0, -1)) : handleKey(String(k))}
                disabled={loading}
                style={{
                  width: 72, height: 58, borderRadius: 12,
                  border: `1px solid ${L.border}`, background: L.surface,
                  fontSize: k === "⌫" ? 18 : 24, fontWeight: 500,
                  color: L.text, cursor: "pointer", fontFamily: sans,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.1s, transform 0.1s",
                }}
                onMouseDown={(e) => { e.currentTarget.style.background = L.inputBg; e.currentTarget.style.transform = "scale(0.96)"; }}
                onMouseUp={(e) => { e.currentTarget.style.background = L.surface; e.currentTarget.style.transform = "scale(1)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = L.surface; e.currentTarget.style.transform = "scale(1)"; }}
              >
                {k}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Floor App ───────────────────────────────────────────────────────────────

function FloorApp({ slug, floorConfig }) {
  useEffect(() => { injectFonts(); }, []);
  const color = floorConfig.color || "#1a1a1a";
  const [tab, setTab] = useState("ask");
  const [bigText, setBigText] = useState(false);
  const [kiosk, setKiosk] = useState(floorConfig.kiosk_auto || new URLSearchParams(window.location.search).has("kiosk"));
  const [wakeLock, setWakeLock] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docSearch, setDocSearch] = useState(""); const [docSearchResults, setDocSearchResults] = useState(null); const [docSearching, setDocSearching] = useState(false); const searchTimerRef = useRef(null);
  const [messages, setMessages] = useState([]); const [chatInput, setChatInput] = useState(""); const [asking, setAsking] = useState(false); const chatEndRef = useRef(null);
  const scopedDocId = new URLSearchParams(window.location.search).get("doc");

  const [pdfOverlay, setPdfOverlay] = useState(null);
  const [procedureView, setProcedureView] = useState(null);
  const pdfCache = useRef({});

  const idleRef = useRef(null);
  const resetIdle = useCallback(() => { if (idleRef.current) clearTimeout(idleRef.current); if (kiosk && floorConfig.idle_timeout) { idleRef.current = setTimeout(() => { setMessages([]); setDocSearchResults(null); setDocSearch(""); setChatInput(""); }, floorConfig.idle_timeout * 60 * 1000); } }, [kiosk, floorConfig.idle_timeout]);
  useEffect(() => { resetIdle(); }, [messages, docSearchResults, resetIdle]);
  useEffect(() => { const h = () => resetIdle(); window.addEventListener("touchstart", h); window.addEventListener("click", h); return () => { window.removeEventListener("touchstart", h); window.removeEventListener("click", h); }; }, [resetIdle]);
  useEffect(() => { api("list-documents", slug).then((d) => setDocs(d.documents || [])); }, [slug]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const toggleWakeLock = async () => { if (wakeLock) { await wakeLock.release(); setWakeLock(null); return; } try { setWakeLock(await navigator.wakeLock.request("screen")); } catch {} };
  const toggleKiosk = () => { if (!kiosk) { document.documentElement.requestFullscreen?.(); setKiosk(true); } else { document.exitFullscreen?.(); setKiosk(false); } };

  const getPdfUrl = async (docId) => {
    if (pdfCache.current[docId]) return pdfCache.current[docId];
    try { const url = await fetchPdfBlob(slug, docId); pdfCache.current[docId] = url; return url; } catch { return null; }
  };
  const openPdf = async (docId, page) => { const url = await getPdfUrl(docId); if (url) setPdfOverlay({ url, page: page || 1 }); };

  const [procedureLoading, setProcedureLoading] = useState(false);
  const openProcedureView = async (steps, sources) => {
    setProcedureLoading(true);
    const doc = getFirstSourceDoc(sources, docs);
    const url = doc ? await getPdfUrl(doc.id) : null;
    setProcedureView({ steps, pdfUrl: url });
    setProcedureLoading(false);
  };

  const handleDocSearch = (v) => { setDocSearch(v); if (searchTimerRef.current) clearTimeout(searchTimerRef.current); if (v.trim().length < 3) { setDocSearchResults(null); return; } searchTimerRef.current = setTimeout(async () => { setDocSearching(true); try { const d = await api("search-documents-grouped", slug, { method: "POST", body: JSON.stringify({ query: v, limit: 10 }) }); setDocSearchResults(d.results || []); } catch { setDocSearchResults([]); } setDocSearching(false); }, 500); };
  const displayDocs = docSearchResults !== null ? docSearchResults.map((sr) => { const doc = docs.find((d) => d.id === sr.document_id); return doc ? { ...doc, _score: sr.score, _snippet: sr.top_snippet, _section: sr.top_section } : null; }).filter(Boolean) : docs;

  const doAsk = async () => { const q = chatInput.trim(); if (!q || asking) return; setMessages((p) => [...p, { role: "user", content: q }]); setChatInput(""); setAsking(true); try { const hist = messages.filter((m) => m.role === "user" || m.role === "assistant").map((m) => ({ role: m.role, content: m.content })); const d = await api("document-qa", slug, { method: "POST", body: JSON.stringify({ question: q, document_ids: scopedDocId ? [scopedDocId] : null, conversation_history: hist }) }); setMessages((p) => [...p, { role: "assistant", content: d.answer, sources: d.sources }]); } catch (e) { setMessages((p) => [...p, { role: "error", content: e.message }]); } setAsking(false); };

  const baseFontSize = bigText ? 18 : 15;

  const headerBtn = {
    padding: "5px 12px", background: L.inputBg, border: `1px solid ${L.border}`,
    borderRadius: 8, color: L.textSoft, fontSize: 11, fontWeight: 600,
    cursor: "pointer", fontFamily: sans,
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: L.bg, fontFamily: sans, fontSize: baseFontSize, color: L.text }}>
      <style>{`
        @keyframes fp { 0%,100% { opacity: 0.2; transform: scale(0.8); } 50% { opacity: 0.6; transform: scale(1.1); } }
      `}</style>

      {/* Header */}
      <div style={{ padding: "14px 20px", background: L.surface, borderBottom: `1px solid ${L.border}`, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 600, fontFamily: serif, fontStyle: "italic" }}>{floorConfig.title}</div>
          <div style={{ fontSize: 11, color: L.muted }}>{floorConfig.account_name}</div>
        </div>
        <button onClick={() => setBigText(!bigText)} style={headerBtn}>{bigText ? "Aa" : "AA"}</button>
        <button onClick={toggleWakeLock} style={{ ...headerBtn, background: wakeLock ? "#f0fdf4" : L.inputBg, borderColor: wakeLock ? "#bbf7d0" : L.border, color: wakeLock ? "#15803d" : L.textSoft }}>
          {wakeLock ? "Screen on" : "Keep awake"}
        </button>
        <button onClick={toggleKiosk} style={headerBtn}>{kiosk ? "Exit fullscreen" : "Fullscreen"}</button>
      </div>

      {/* Scoped doc banner */}
      {scopedDocId && (
        <div style={{ padding: "10px 20px", background: L.inputBg, borderBottom: `1px solid ${L.border}`, fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 10 }}>
          {docs.find((d) => d.id === scopedDocId)?.name || "Loading..."}
          <button onClick={() => openPdf(scopedDocId)} style={{ padding: "4px 10px", background: L.surface, border: `1px solid ${L.border}`, borderRadius: 6, color: L.textSoft, fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: sans }}>View PDF</button>
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {/* Documents tab */}
        {tab === "documents" && (
          <div style={{ padding: 20 }}>
            <div style={{ position: "relative", marginBottom: 18 }}>
              <input
                value={docSearch} onChange={(e) => handleDocSearch(e.target.value)}
                placeholder="Search or browse documents..."
                style={{
                  width: "100%", padding: "14px 18px", paddingRight: docSearchResults !== null ? 40 : 18,
                  border: `1px solid ${docSearchResults !== null ? "#bfdbfe" : L.border}`,
                  borderRadius: 12, fontSize: baseFontSize, outline: "none", fontFamily: sans,
                  boxSizing: "border-box", color: L.text, background: L.surface,
                }}
              />
              {docSearching && <span style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", color: L.dim }}>⟳</span>}
              {docSearchResults !== null && !docSearching && <button onClick={() => { setDocSearch(""); setDocSearchResults(null); }} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: L.dim, fontSize: 16, cursor: "pointer" }}>×</button>}
            </div>
            {docSearchResults !== null && <div style={{ marginBottom: 14, fontSize: 13, color: L.muted }}>{docSearchResults.length} result{docSearchResults.length !== 1 ? "s" : ""}</div>}
            {displayDocs.length === 0 ? (
              <div style={{ textAlign: "center", color: L.dim, padding: 40 }}>{docSearchResults !== null ? "No matches." : "No documents."}</div>
            ) : displayDocs.map((d) => (
              <div key={d.id} style={{ background: L.surface, borderRadius: 12, padding: 18, marginBottom: 10, border: `1px solid ${L.borderLight}` }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: baseFontSize, fontWeight: 600 }}>{d.name}</span>
                      {d._score != null && <span style={{ fontSize: 11, fontWeight: 600, color: "#1d4ed8", background: "#eff6ff", padding: "2px 8px", borderRadius: 4 }}>{(d._score * 100).toFixed(0)}%</span>}
                    </div>
                    <div style={{ fontSize: 12, color: L.muted, marginTop: 3 }}>{[d.category, d.pages ? `${d.pages} pages` : null].filter(Boolean).join(" · ")}</div>
                    {d.summary && !d._snippet && <div style={{ fontSize: 13, color: L.textSoft, marginTop: 8, lineHeight: 1.6 }}>{d.summary}</div>}
                    {d._snippet && <div style={{ fontSize: 13, color: L.textSoft, marginTop: 8, fontStyle: "italic", borderLeft: `3px solid ${L.border}`, paddingLeft: 12, lineHeight: 1.6 }}>"{d._snippet}"</div>}
                  </div>
                  {d.has_download && (
                    <button onClick={() => openPdf(d.id)} style={{ padding: "9px 16px", background: L.inputBg, border: `1px solid ${L.border}`, borderRadius: 8, color: L.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans, flexShrink: 0 }}>View</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Ask tab */}
        {tab === "ask" && (
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
              {messages.length === 0 && (
                <div style={{ textAlign: "center", padding: "60px 20px" }}>
                  <div style={{ fontSize: 28, fontFamily: serif, fontStyle: "italic", color: L.text, marginBottom: 8 }}>Ask anything</div>
                  <div style={{ fontSize: 14, color: L.muted }}>Questions about the documents will be answered using AI.</div>
                </div>
              )}
              {messages.map((m, i) => {
                const proc = m.role === "assistant" ? extractProcedure(m.content) : null;
                return (
                  <div key={i} style={{ marginBottom: 16 }}>
                    {m.role === "user" && (
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <div style={{ maxWidth: "85%", padding: "13px 18px", background: L.text, color: L.bg, borderRadius: "16px 16px 4px 16px", fontSize: baseFontSize, lineHeight: 1.6 }}>{m.content}</div>
                      </div>
                    )}
                    {m.role === "assistant" && (
                      <div>
                        <div style={{ padding: "16px 18px", background: L.surface, borderRadius: "16px 16px 16px 4px", border: `1px solid ${L.borderLight}` }}>
                          <RenderMarkdown text={m.content} fontSize={baseFontSize} />
                          {proc && (
                            <button onClick={() => openProcedureView(proc, m.sources)} disabled={procedureLoading} style={{ marginTop: 12, padding: "11px 18px", background: L.text, border: "none", borderRadius: 10, color: L.bg, fontSize: 14, fontWeight: 600, cursor: procedureLoading ? "not-allowed" : "pointer", fontFamily: sans, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: procedureLoading ? 0.5 : 1 }}>
                              {procedureLoading ? "Loading PDF..." : `Open procedure (${proc.length} steps)`}
                            </button>
                          )}
                        </div>
                        {m.sources?.length > 0 && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                            {m.sources.map((s, j) => {
                              const srcDoc = docs.find((dd) => dd.name === s.filename);
                              return (
                                <div key={j} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: L.surface, border: `1px solid ${L.borderLight}`, borderRadius: 10 }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, color: L.textSoft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {s.filename}{s.section && <span style={{ color: L.muted }}> · {s.section}</span>}
                                    </div>
                                  </div>
                                  {s.score && <span style={{ fontSize: 11, fontWeight: 600, color: "#1d4ed8", flexShrink: 0 }}>{typeof s.score === "number" ? `${(s.score * 100).toFixed(0)}%` : ""}</span>}
                                  {srcDoc && <button onClick={() => openPdf(srcDoc.id, s.page)} style={{ padding: "4px 10px", background: L.inputBg, border: `1px solid ${L.border}`, borderRadius: 6, color: L.textSoft, fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: sans }}>View</button>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    {m.role === "error" && <div style={{ padding: 14, background: "#fef2f2", borderRadius: 12, color: "#dc2626", fontSize: 14, border: "1px solid #fecaca" }}>{m.content}</div>}
                  </div>
                );
              })}
              {asking && <div style={{ display: "flex", gap: 6, padding: "12px 0" }}>{[0, 1, 2].map((j) => <div key={j} style={{ width: 8, height: 8, borderRadius: "50%", background: L.text, animation: `fp 1.2s ease-in-out ${j * 0.15}s infinite` }} />)}</div>}
              <div ref={chatEndRef} />
            </div>
            <div style={{ padding: "12px 20px", borderTop: `1px solid ${L.border}`, background: L.surface, display: "flex", gap: 10, flexShrink: 0 }}>
              <input
                value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && doAsk()}
                placeholder="Ask a question..."
                disabled={asking}
                style={{ flex: 1, padding: "14px 18px", border: `1px solid ${L.border}`, borderRadius: 12, fontSize: baseFontSize, outline: "none", fontFamily: sans, color: L.text, background: L.inputBg }}
              />
              <button onClick={doAsk} disabled={asking || !chatInput.trim()} style={{ padding: "14px 24px", background: L.text, border: "none", borderRadius: 12, color: L.bg, fontSize: baseFontSize, fontWeight: 600, cursor: "pointer", fontFamily: sans, opacity: (asking || !chatInput.trim()) ? 0.3 : 1 }}>Ask</button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div style={{ display: "flex", borderTop: `1px solid ${L.border}`, background: L.surface, flexShrink: 0 }}>
        {[
          { id: "documents", label: "Documents" },
          { id: "ask", label: "Ask AI" },
        ].map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id} onClick={() => setTab(t.id)}
              style={{
                flex: 1, padding: "14px 0", background: "none", border: "none",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                color: active ? L.text : L.muted, cursor: "pointer", fontFamily: sans,
                borderTop: active ? `2px solid ${L.text}` : `2px solid transparent`,
                fontWeight: active ? 600 : 400, fontSize: 12,
                transition: "color 0.15s",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {pdfOverlay && <PDFOverlay pdfUrl={pdfOverlay.url} page={pdfOverlay.page} onClose={() => setPdfOverlay(null)} />}
      {procedureView && <ProcedurePDFView steps={procedureView.steps} pdfUrl={procedureView.pdfUrl} color={color} onClose={() => setProcedureView(null)} />}
    </div>
  );
}

// ─── Export ──────────────────────────────────────────────────────────────────

export default function FloorAccess() {
  const [authed, setAuthed] = useState(false);
  const [floorConfig, setFloorConfig] = useState(null);
  const slug = new URLSearchParams(window.location.search).get("account") || "morryde";
  if (!authed) return <PinScreen slug={slug} onSuccess={(d) => { setFloorConfig(d.config); setAuthed(true); }} />;
  return <FloorApp slug={slug} floorConfig={floorConfig} />;
}
